import * as EventTracker from '@src/analytics/EventTracker';
import { findQuoteMatchRange } from '@src/components/content-components/QuoteCard/findQuoteMatchRange';
import { useClipSearchService } from '@src/hooks/useClipSearchService';
import { useTranscriptAnswerService } from '@src/hooks/useTranscriptAnswerService';
import { saveProducedClips } from '@src/services/AIClipProducer';
import { useContentStudioUI } from '@src/stores-v2/StoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import {
  pickNextSavedSegmentColor,
  savedSegmentDefaultName,
} from '@src/utility/savedSegmentColors';
import { dispatchTranscriptIndexJump } from '@src/utility/transcriptEvents';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCandidateHighlights } from './useCandidateHighlights';
import { useClipSearchChat } from './useClipSearchChat';

import type { FindClipsPanelProps } from './FindClipsPanel';
import type {
  ExternalPlayerControls,
  VideoTimeRange,
} from './TranscriptVideoPlayer';
import type { UseTopicFilteringResult } from './useTopicFiltering';
import type { ClipFragment } from '@src/services/AIClipProducer';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { RootStore } from '@src/stores-v2/RootStore';
import type { TranscriptionStore } from '@src/stores-v2/TranscriptionStore';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type { useFlagsCombination } from '@src/utility/useFlagsCombination';

const logger = LogManager.getLogger('component:useCoProducer');

type Flags = ReturnType<typeof useFlagsCombination>;

export interface UseCoProducerInput {
  /** Flag on and not a translation reviewer. */
  enabled: boolean;
  videoCreator: IVideoCreatorStore;
  rootStore: RootStore;
  /** The tab's adapter — scopes transcript jumps to this tab's Transcript. */
  adapter: ITranscriptVideoCreator;
  transcription: Pick<TranscriptionStore, 'getTranscriptionText'>;
  flags: Pick<
    Flags,
    | 'contentStudioClipProducerEnableTextVerification'
    | 'contentStudioClipProducerFilterTranscriptBySpeaker'
    | 'contentStudioClipProducerChunkSize'
    | 'enableLightweightClipSave'
    | 'enableBackgroundClipSave'
  >;
  originalElements: TranscriptElement[] | undefined;
  storyDurationSec: number;
  playerControls: ExternalPlayerControls | null;
  savedSegmentService: {
    saveSegment: (input: {
      name: string;
      startIndex: number;
      endIndex: number;
      color?: string;
    }) => Promise<unknown>;
  };
  /** What owns the transcript's highlight props when no candidates are live. */
  topicFilter: Pick<
    UseTopicFilteringResult,
    | 'visibleParagraphIds'
    | 'highlightedParagraphIds'
    | 'paragraphHighlightColors'
    | 'activeParagraphId'
    | 'topicFilterMode'
  >;
  /** What owns the player's playback windows when no candidates are live. */
  highlightPlaybackRanges: VideoTimeRange[] | null;
}

export interface UseCoProducerResult {
  transcriptHighlightProps: {
    visibleParagraphIds: UseTopicFilteringResult['visibleParagraphIds'];
    highlightedParagraphIds: UseTopicFilteringResult['highlightedParagraphIds'];
    paragraphHighlightColors: UseTopicFilteringResult['paragraphHighlightColors'];
    activeParagraphId: UseTopicFilteringResult['activeParagraphId'];
    ownsShowOnly: boolean;
  };
  playbackRanges: VideoTimeRange[] | null;
  /** Everything `FindClipsPanel` takes except the player's own state. */
  panelProps: Omit<FindClipsPanelProps, 'isPlaying' | 'onTogglePlayAll'>;
}

/**
 * The Co-Producer tab's controller (PLAT-11806): the chat, its candidates,
 * the actions on a candidate, and which source owns the transcript's
 * highlight props and the player's playback windows.
 *
 * Host-owned, like `useTopicFiltering`: `TranscriptSpeakersContent` renders
 * the tab strip and passes the result down as data.
 */
export function useCoProducer(input: UseCoProducerInput): UseCoProducerResult {
  const {
    enabled,
    videoCreator,
    rootStore,
    adapter,
    transcription,
    flags,
    originalElements,
    storyDurationSec,
    playerControls,
    savedSegmentService,
    topicFilter,
    highlightPlaybackRanges,
  } = input;
  const contentStudioUI = useContentStudioUI();

  const clipSearchService = useClipSearchService(videoCreator, rootStore);
  const transcriptAnswerService = useTranscriptAnswerService(
    videoCreator,
    rootStore,
  );
  const clipSearchConfig = useMemo(
    () => ({
      enableTextVerification:
        flags.contentStudioClipProducerEnableTextVerification,
      enableSpeakerFiltering:
        flags.contentStudioClipProducerFilterTranscriptBySpeaker,
      chunkSize: flags.contentStudioClipProducerChunkSize,
    }),
    [
      flags.contentStudioClipProducerEnableTextVerification,
      flags.contentStudioClipProducerFilterTranscriptBySpeaker,
      flags.contentStudioClipProducerChunkSize,
    ],
  );
  const transcriptionTextForSearch = useMemo(
    () =>
      originalElements
        ? transcription.getTranscriptionText(originalElements)
        : '',
    [transcription, originalElements],
  );
  const clipChat = useClipSearchChat({
    service: clipSearchService,
    answerService: transcriptAnswerService,
    transcriptElements: originalElements,
    transcriptionText: transcriptionTextForSearch,
    originalVideo: videoCreator.story?.originalVideo,
    originalDuration: storyDurationSec,
    config: clipSearchConfig,
  });
  const candidateHighlights = useCandidateHighlights({
    candidates: clipChat.candidates,
    transcriptElements: originalElements,
    searchId: clipChat.searchId,
  });

  // PLAT-11806 DS3/DS5 — `visibleParagraphIds`, `highlightedParagraphIds`,
  // `paragraphHighlightColors` and `activeParagraphId` are single-valued
  // props, and the topic filter already writes them. Rather than merge two
  // meanings into one gutter rail, the live result set takes them over while
  // it exists and hands them straight back when the chat is cleared.
  const candidatesOwnHighlights = enabled && candidateHighlights.active;
  // Same precedence on the playback channel: while candidates are live the
  // player auditions them (all of them, or just the selected one), and the
  // Highlights filter gets the channel back the moment they are cleared.
  const effectivePlaybackRanges = candidatesOwnHighlights
    ? candidateHighlights.playbackRanges
    : highlightPlaybackRanges;
  const transcriptHighlightProps = candidatesOwnHighlights
    ? {
        visibleParagraphIds: candidateHighlights.visibleParagraphIds,
        highlightedParagraphIds: candidateHighlights.highlightedParagraphIds,
        paragraphHighlightColors: candidateHighlights.paragraphHighlightColors,
        activeParagraphId: candidateHighlights.activeParagraphId,
        ownsShowOnly: candidateHighlights.showOnly,
      }
    : {
        visibleParagraphIds: topicFilter.visibleParagraphIds,
        highlightedParagraphIds: topicFilter.highlightedParagraphIds,
        paragraphHighlightColors: topicFilter.paragraphHighlightColors,
        activeParagraphId: topicFilter.activeParagraphId,
        ownsShowOnly: topicFilter.topicFilterMode === 'show-only',
      };

  /**
   * Picking a card plays that passage in the docked player below.
   *
   * Deliberately an effect rather than a call inside the click handler: the
   * click sets `selectedKey`, which is what narrows `playbackSegments` to that
   * one window, and the player only honours the narrowed window once the prop
   * has landed. Calling `play()` synchronously would start against the
   * PREVIOUS segments — every candidate, from the top.
   *
   * Once playing, the existing gap-skipping logic does the rest: it seeks to
   * the window's start and pauses in place at its end (PLAT-10949), so a click
   * auditions exactly that candidate and stops.
   *
   * Deselecting does not pause. The ranges simply widen back to every
   * candidate, which is the same thing Play All does.
   */
  const autoPlayedCandidateKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = candidateHighlights.selectedKey;
    // Ref-guarded so a new `playerControls` identity (the player remounts on
    // resolution changes) cannot replay a candidate the user already heard.
    if (key === autoPlayedCandidateKeyRef.current) return;
    autoPlayedCandidateKeyRef.current = key;
    if (!key || !enabled) return;
    if (!playerControls?.isReady) return;
    playerControls.play();
  }, [candidateHighlights.selectedKey, enabled, playerControls]);

  const [savingCandidateKeys, setSavingCandidateKeys] = useState<Set<string>>(
    new Set(),
  );
  const [savedCandidateKeys, setSavedCandidateKeys] = useState<Set<string>>(
    new Set(),
  );

  // Scroll the transcript to a candidate and put the playhead at its start.
  // The jump is a scoped window event rather than a Transcript prop — the
  // same channel the Reel Editor uses (PLAT-11061), per guardrail 5.
  const jumpToTranscriptIndex = useCallback(
    (index: number) => {
      dispatchTranscriptIndexJump({ index }, adapter);
    },
    [adapter],
  );

  const revealCandidate = useCallback(
    (candidate: {
      fragment: { transcriptPosition: { startIndex: number } };
    }) => {
      const { startIndex } = candidate.fragment.transcriptPosition;
      if (startIndex == null) return;
      jumpToTranscriptIndex(startIndex);
    },
    [jumpToTranscriptIndex],
  );

  // Where a quotation in an answer sits. Same matcher the pull-quote underline
  // uses, so a paraphrased or elided quote still lands: it scores IDF-weighted
  // word overlap rather than looking for the string.
  const locateQuote = useCallback(
    (quote: string) => {
      if (!originalElements) return null;
      const range = findQuoteMatchRange(originalElements, quote);
      return range ? range.start : null;
    },
    [originalElements],
  );

  // Saving mirrors the Clip Producer modal's save: same options, same
  // recently-saved marking, same failure toast.
  const saveCandidate = useCallback(
    async (candidate: { key: string; fragment: ClipFragment }) => {
      if (savingCandidateKeys.has(candidate.key)) return;
      setSavingCandidateKeys(prev => new Set(prev).add(candidate.key));
      try {
        const result = await saveProducedClips(
          videoCreator,
          [candidate.fragment],
          'content-studio',
          false,
          {
            enableLightweightClipSave: flags.enableLightweightClipSave,
            useBackgroundQueue: flags.enableBackgroundClipSave,
          },
        );
        const savedIds =
          result.mode === 'queued' ? result.tempIds : result.savedIds;
        if (savedIds.length > 0) {
          contentStudioUI.markClipsSaved(savedIds);
        }
        setSavedCandidateKeys(prev => new Set(prev).add(candidate.key));
        EventTracker.trackClipSaved({
          story_id: videoCreator.story?.id,
          clips_count: 1,
          source_platform: 'content-studio-clip-chat',
          as_reel: false,
        });
      } catch (e) {
        logger.error('Failed to save a clip from the Find Clips chat', {
          ...extractErrorInfo(e),
          storyId: videoCreator.story?.id,
        });
        videoCreator.showToast({
          state: 'error',
          message: 'Saving the clip failed. Please try again.',
        });
      } finally {
        setSavingCandidateKeys(prev => {
          const next = new Set(prev);
          next.delete(candidate.key);
          return next;
        });
      }
    },
    [
      savingCandidateKeys,
      videoCreator,
      contentStudioUI,
      flags.enableLightweightClipSave,
      flags.enableBackgroundClipSave,
    ],
  );

  // Highlighting a Co-Producer candidate is the transcript's own Highlight
  // action pointed at a range the chat found, so it goes through the same
  // service and is indistinguishable from one made by hand. The color is
  // picked here rather than left to the service because the default name is
  // derived from it ("Violet Highlight").
  const highlightingCandidateKeysRef = useRef(new Set<string>());
  const [highlightedCandidateKeys, setHighlightedCandidateKeys] = useState<
    Set<string>
  >(new Set());

  const highlightCandidate = useCallback(
    async (candidate: { key: string; fragment: ClipFragment }) => {
      if (highlightingCandidateKeysRef.current.has(candidate.key)) return;
      highlightingCandidateKeysRef.current.add(candidate.key);
      // Jump first, so the wash appears where the user is already looking
      // rather than somewhere off-screen. Reveal only — selecting the
      // candidate would start the docked player (see the auto-play effect),
      // and highlighting is not a request to watch it.
      revealCandidate(candidate);
      const { startIndex, endIndex } = candidate.fragment.transcriptPosition;
      const color = pickNextSavedSegmentColor(
        Object.values(videoCreator.story?.savedSegments ?? {})
          .map(group => group.color)
          .filter((c): c is string => !!c),
      );
      const name = savedSegmentDefaultName(color);
      try {
        const key = await savedSegmentService.saveSegment({
          name,
          startIndex,
          endIndex,
          color,
        });
        if (key == null) {
          videoCreator.showToast({
            state: 'error',
            message: 'Failed to save highlight. Please try again.',
          });
          return;
        }
        setHighlightedCandidateKeys(prev => new Set(prev).add(candidate.key));
        videoCreator.showToast({ state: 'success', message: `${name} saved` });
      } finally {
        highlightingCandidateKeysRef.current.delete(candidate.key);
      }
    },
    [revealCandidate, savedSegmentService, videoCreator],
  );

  return {
    transcriptHighlightProps,
    playbackRanges: effectivePlaybackRanges,
    panelProps: {
      messages: clipChat.messages,
      isSearching: clipChat.isSearching,
      mode: clipChat.mode,
      onModeChange: clipChat.setMode,
      targetDuration: clipChat.targetDuration,
      onTargetDurationChange: clipChat.setTargetDuration,
      onSendMessage: clipChat.sendMessage,
      onCancelSearch: clipChat.cancelSearch,
      candidates: candidateHighlights.visibleCandidates,
      totalCandidates: candidateHighlights.totalCandidates,
      expanded: candidateHighlights.expanded,
      onToggleExpanded: candidateHighlights.toggleExpanded,
      onSelectCandidate: candidateHighlights.selectCandidate,
      onRevealCandidate: revealCandidate,
      onSaveCandidate: saveCandidate,
      savingKeys: savingCandidateKeys,
      savedKeys: savedCandidateKeys,
      onHighlightCandidate: highlightCandidate,
      highlightedKeys: highlightedCandidateKeys,
      locateQuote,
      onJumpToQuote: jumpToTranscriptIndex,
      showOnly: candidateHighlights.showOnly,
      onShowOnlyChange: candidateHighlights.setShowOnly,
    },
  };
}
